import React, {memo, useMemo} from "react";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faSignOut} from "@fortawesome/pro-light-svg-icons/faSignOut";
import {faArrowUpRightFromSquare} from "@fortawesome/free-solid-svg-icons/faArrowUpRightFromSquare";
import {NavLink, useLocation} from "react-router-dom";
import {shallowEqual, useDispatch, useSelector} from "react-redux";
import {isEmpty} from "es-toolkit/compat";
import {
  faBookOpen,
  faCalendar,
  faCartShopping,
  faCircleNotch,
  faCommentQuestion,
  faHouse,
  faQuestion,
} from "@fortawesome/pro-regular-svg-icons";
import {faHouse as faHouseSolid} from "@fortawesome/pro-solid-svg-icons";

import {logout} from "@/features/auth";
import {toggleSidebarMenu} from "@/helpers/toggleSidebarMenu.js";
import {config} from "@/config";
import {selectPartnersDetails} from "@/features/partners/partnersSelectors";
import {useWhiteLabelTheme} from "@/hooks/useWhiteLabelTheme/useWhiteLabelTheme";
import RenderIf from "@/components/Global/RenderIf/RenderIf";

import styles from "./QuickLinksDesktop.module.scss";

const ICON_MAP = {
  faCalendar: faCalendar,
  faCommentQuestion: faCommentQuestion,
  faHouse: faHouse,
  faHouseSolid: faHouseSolid,
  faCircleNotch: faCircleNotch,
  faCartShopping: faCartShopping,
  faBookOpen: faBookOpen,
};

export default memo(function QuickLinksDesktop({routes: defaultRoutes, className = "", ...rest}) {
  const {themeConfig} = useWhiteLabelTheme();
  const {pathname} = useLocation();
  const partnerDetails = useSelector(selectPartnersDetails, shallowEqual);
  const dispatch = useDispatch();

  const handleLogout = () => {
    dispatch(logout());
  };

  const routes = useMemo(() => {
    const accountMenu = defaultRoutes.find(route => route.name === "Account");

    if (!isEmpty(themeConfig?.desktopNav)) {
      const newRoutes = themeConfig?.desktopNav.map((item, index) => ({
        ...item,
        icon: item.icon ? (
          <FontAwesomeIcon
            icon={ICON_MAP[item.icon] || faQuestion}
            size="2xl"
            style={item.icon === "faCircleNotch" ? {transform: "rotate(180deg)"} : {}}
          />
        ) : null,
        activeIcon: item.activeIcon ? (
          <FontAwesomeIcon
            icon={ICON_MAP[item.activeIcon] || faQuestion}
            size="2xl"
            style={item.icon === "faCircleNotch" ? {transform: "rotate(180deg)"} : {}}
          />
        ) : null,
        order: item.order || index + 1,
      }));

      accountMenu.order = newRoutes.length + 1;
      newRoutes.push(accountMenu);

      return newRoutes
        .sort((a, b) => a.order - b.order)
        .filter((route, index, self) => index === self.findIndex(r => r.path === route.path));
    }

    return defaultRoutes;
  }, [themeConfig, defaultRoutes]);

  const partnerLogo = partnerDetails?.partnerLogo ? `${partnerDetails?.partnerLogo}` : "";
  const showPartnerLogo = config.websiteId == 3 && partnerLogo;

  if (partnerLogo && showPartnerLogo && !window.config.isWhiteLabelApp) {
    document.documentElement.style.setProperty("--brand-logo", `url(${partnerLogo})`);
  }

  return (
    <nav {...rest} className={`${styles.navWrapper} ${className}`}>
      <NavLink aria-label="Go to home" className={`${styles.navItem}`} to={"/"}>
        <div className={styles.brandLogo} data-white-label={window.config.isWhiteLabelApp}></div>
      </NavLink>

      {routes?.map((route, index) => {
        let target = route.target ? route.target : "_self";
        let url = route?.path;
        let isActive = url === pathname;
        const isMyAccountActive = pathname.includes("my-account");

        const icon = isActive && route?.activeIcon ? route?.activeIcon : route?.icon;
        let routeName = route?.name;

        if (pathname.includes("health-score") && url === "/results") {
          isActive = true;
        }

        if (url === "") {
          isActive = false;
        }

        if (url === "" && pathname.includes("my-account")) {
          isActive = true;
        }

        const isExternalLink = url.includes("http");

        if (route.hasToOpenMenu) {
          return (
            <button
              key={index}
              onClick={toggleSidebarMenu}
              aria-expanded="false"
              id="sidebarButton"
              className={`${isMyAccountActive ? styles.active : ""} ${styles.navItem} ${route.className}`}>
              <span className="position-relative">
                {isMyAccountActive ? route?.activeIcon : icon}
                <RenderIf isTrue={isExternalLink}>
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} className={styles.externalLink} />
                </RenderIf>
              </span>
              {routeName}
            </button>
          );
        }

        if (isExternalLink) {
          const url = new URL(route.path);
          if (config.isNativeApp) {
            url.searchParams.set("external", "true");
          }

          return (
            <a key={index} href={url} target="_blank" className={`${styles.navItem} ${route.className}`}>
              <span className="position-relative">
                {icon}
                <RenderIf isTrue={isExternalLink}>
                  <FontAwesomeIcon icon={faArrowUpRightFromSquare} className={styles.externalLink} />
                </RenderIf>
              </span>
              {routeName}
            </a>
          );
        }

        return (
          <NavLink
            key={index}
            className={({isActive}) => {
              if (pathname.includes("health-score") && route.path === "/results") {
                return `${styles.active} ${styles.navItem} ${route.className}`;
              }

              if (route.path === "") {
                return `${styles.navItem} ${route.className}`;
              }

              return isActive
                ? `${styles.active} ${styles.navItem} ${route.className}`
                : `${styles.navItem} ${route.className}`;
            }}
            to={url}
            target={isExternalLink ? "_blank" : target}>
            <span className="position-relative">
              {icon}
              <RenderIf isTrue={isExternalLink}>
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} className={styles.externalLink} />
              </RenderIf>
            </span>
            {routeName}
          </NavLink>
        );
      })}

      <button className={`${styles.navItem} mt-auto`} onClick={handleLogout}>
        <FontAwesomeIcon icon={faSignOut} size="2xl" className="fa-fw" />
        Logout
      </button>
    </nav>
  );
});
